// 逾期任务「整体改期」的纯逻辑 —— web / mobile 共用一份（避免两端手工分叉，见
// .claude/rules/local-first-guardrails.md D 节）。
//
// 把一个任务的日期形态整体「平移」到目标日期 (YYYY-MM-DD)，**保留形态**：
//   - dueDate（全天）   → dueDate = target
//   - dueAt（时点）     → 保留原时分秒，仅换日期（与 recurrence.advanceTaskDate 的 dueAt 处理一致：
//                         全程走 getUTC*，结果与运行进程 TZ 无关）
//   - start/end（区间） → 平移整段，使 startDate = target，保留原跨度天数
//   - 其它（无显式日期）→ 落到 dueDate = target（保守兜底；taskIsOverdue 不会把这类判为逾期，
//                         实际不会命中此分支，但保证函数对任意输入有定义）
//
// **不触碰 recurrence**：调用方只把返回的日期字段 patch 进 store，重复规则原样保留。
// 这与 Todoist 一致 —— 重新安排一个 recurring 任务只移动当前这次的日期，规则不变。

export interface TaskDateFields {
  dueDate: string | null;
  dueAt: string | null;
  startDate: string | null;
  endDate: string | null;
}

const DATE_ONLY_RE = /^\d{4}-\d{2}-\d{2}$/;
const MS_PER_DAY = 86_400_000;

// 两个 date-only 串之间的「日历日差」(b - a)，按 UTC 午夜锚定，TZ 无关。
function dayDelta(from: string, to: string): number {
  const a = Date.parse(`${from}T00:00:00.000Z`);
  const b = Date.parse(`${to}T00:00:00.000Z`);
  if (Number.isNaN(a) || Number.isNaN(b)) return 0;
  return Math.round((b - a) / MS_PER_DAY);
}

function addUtcDays(date: string, days: number): string {
  const d = new Date(`${date}T00:00:00.000Z`);
  d.setUTCDate(d.getUTCDate() + days);
  return d.toISOString().slice(0, 10);
}

export function rescheduleToDate(task: TaskDateFields, targetDate: string): TaskDateFields {
  // 目标日期非法 → 原样返回，绝不把脏值写进 store（渲染期 safeParseISO 之外的第二道防线）。
  if (!DATE_ONLY_RE.test(targetDate)) {
    return {
      dueDate: task.dueDate,
      dueAt: task.dueAt,
      startDate: task.startDate,
      endDate: task.endDate,
    };
  }

  const allDay: TaskDateFields = {
    dueDate: targetDate,
    dueAt: null,
    startDate: null,
    endDate: null,
  };

  // 区间任务：平移整段，保留跨度（start→target，end 跟着平移相同天数）。
  if (task.startDate && task.endDate) {
    const span = Math.max(0, dayDelta(task.startDate, task.endDate));
    return {
      dueDate: null,
      dueAt: null,
      startDate: targetDate,
      endDate: addUtcDays(targetDate, span),
    };
  }

  // 时点任务：保留原时分秒，仅换日期。
  if (task.dueAt) {
    const orig = new Date(task.dueAt);
    if (Number.isNaN(orig.getTime())) return allDay; // 脏 dueAt → 降级为全天，避免 toISOString 抛错
    const next = new Date(`${targetDate}T00:00:00.000Z`);
    next.setUTCHours(orig.getUTCHours(), orig.getUTCMinutes(), orig.getUTCSeconds(), 0);
    return {
      dueDate: null,
      dueAt: next.toISOString(),
      startDate: null,
      endDate: null,
    };
  }

  // 全天任务 + 兜底。
  return allDay;
}
